import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormInput,
  FormTextarea,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Col
} from "shards-react";

// Predefined adjustment notes
const PREDEFINED_NOTES = [
  "Courtesy discount due to first-time violation.",
  "Adjustment following customer dispute review.",
  "Reduction for payment within grace period.",
  "Partial discount due to mitigating circumstances."
];

const AdjustBalanceModal = ({ 
  isOpen, 
  toggle, 
  citation, 
  onAdjust 
}) => {
  const [newAmount, setNewAmount] = useState(0);
  const [displayAmount, setDisplayAmount] = useState('');
  const [adjustmentNote, setAdjustmentNote] = useState('');
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const [selectedPercentage, setSelectedPercentage] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('percentage'); // 'percentage' or 'custom'
  const [selectedNoteIndex, setSelectedNoteIndex] = useState(-1); // -1 means custom note

  useEffect(() => {
    if (citation && isOpen) {
      // Set the calculation amount but not the display amount
      setNewAmount(parseFloat(citation.total).toFixed(2));
      setDisplayAmount('');
      setDisplayPercentage(0);
      setAdjustmentNote('');
      setSelectedPercentage(null);
      setAdjustmentType('percentage');
      setSelectedNoteIndex(-1);
    }
  }, [citation, isOpen]);

  useEffect(() => {
    if (citation && selectedPercentage) {
      const currentTotal = parseFloat(citation.total);
      const discountedAmount = currentTotal * (1 - selectedPercentage / 100);
      const formattedAmount = discountedAmount.toFixed(2);
      setNewAmount(formattedAmount);
      setDisplayAmount(formattedAmount);
      setDisplayPercentage(selectedPercentage);
    }
  }, [selectedPercentage, citation]);

  const getCurrentTotal = () => {
    if (!citation) return "0.00";
    return parseFloat(citation.total).toFixed(2);
  };

  const handleAdjust = () => {
    if (window.confirm('Are you sure you want to adjust the balance?')) {
      onAdjust(citation, newAmount, adjustmentNote);
      toggle();
    }
  };

  const handleCustomAmountChange = (value) => {
    console.log('handleCustomAmountChange', value);
    setDisplayAmount(value);
    
    // If the value is empty, use the original amount for calculations
    if (value === '') {
      setNewAmount(parseFloat(citation.total).toFixed(2));
      setSelectedPercentage(0);
      return;
    }
    
    // Otherwise process normally
    setNewAmount(value);
    
    if (citation && citation.total > 0) {
      // Calculate percentage based on entered amount
      const currentTotal = parseFloat(citation.total);
      const percentage = Math.round(100 - ((parseFloat(value) / currentTotal) * 100));
      
      // Only update if it's a valid percentage
      if (percentage >= 0 && percentage <= 100) {
        setDisplayPercentage(percentage);
      }
    }
  };

  const calculateDiscount = () => {
    const currentTotal = parseFloat(citation.total);
    const newAmountValue = parseFloat(newAmount);
    return (currentTotal - newAmountValue).toFixed(2);
  };
  
  const handleSelectCustom = () => {
    if (adjustmentType !== 'custom') {
      setAdjustmentType('custom');
      // When switching to custom mode, pre-populate with the current calculation amount
      setDisplayAmount(newAmount);
    }
  };
  
  const handleSelectNote = (index) => {
    setSelectedNoteIndex(index);
    if (index >= 0) {
      setAdjustmentNote(PREDEFINED_NOTES[index]);
    }
  };

  if (!citation) return null;

  return (
    <Modal size={'md'} open={isOpen} toggle={toggle} className="adjust-balance-modal">
      <style>
        {`
          .adjust-balance-modal .modal-content {
            border-radius: 6px;
            border: none;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
          }
          
          .adjust-balance-modal .modal-header {
            background-color: #fff;
            border-bottom: 1px solid #eaedf3;
            padding: 12px 16px;
          }
          
          .adjust-balance-modal .modal-title {
            font-weight: 600;
            color: #3D5170;
          }
          
          .field-label {
            font-size: 11px;
            text-transform: uppercase;
            color: #818ea3;
            margin-bottom: 2px;
            font-weight: 500;
          }
          
          .field-value {
            font-size: 14px;
            color: #495057;
          }
          
          .adjustment-section {
            background-color: #fff;
            border: 1px solid #eaedf3;
            border-radius: 6px;
            padding: 10px;
            margin-bottom: 10px;
            transition: all 0.2s ease;
          }
          
          .adjustment-section.active {
            border-color: rgba(33, 170, 115, 0.4);
            box-shadow: 0 0 0 3px rgba(33, 170, 115, 0.1);
          }
          
          .adjustment-section-title {
            font-size: 14px;
            font-weight: 600;
            color: #3D5170;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
          }
          
          .adjustment-section-title .radio {
            margin-right: 10px;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 2px solid #dfe3e9;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }
          
          .adjustment-section-title .radio.selected {
            border-color: #21AA73;
          }
          
          .adjustment-section-title .radio.selected:after {
            content: "";
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #21AA73;
          }
          
          .percentage-buttons {
            display: flex;
            justify-content: space-between;
          }
          
          .percentage-button {
            cursor: pointer;
            height: 38px;
            width: 58px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            border: 1px solid #dfe3e9;
            background-color: #f8f9fa;
            color: #495057;
            font-weight: 500;
            font-size: 14px;
          }
          
          .percentage-button.active {
            background-color: rgba(33, 170, 115, 0.1);
            border-color: #21AA73;
            color: #21AA73;
          }
          
          .percentage-button:hover:not(.active) {
            border-color: #c8cbd3;
            background-color: #f1f3f5;
          }
          
          .amount-summary-box {
            background-color: rgba(33, 170, 115, 0.05);
            border: 1px solid rgba(33, 170, 115, 0.2);
            border-radius: 4px;
            padding: 8px 12px;
            margin-top: 10px;
            margin-bottom: 10px;
          }
          
          .amount-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            color: #495057;
            padding: 2px 0;
          }
          
          .amount-row.total {
            margin-top: 2px;
            padding-top: 4px;
            border-top: 1px dashed rgba(33, 170, 115, 0.2);
            font-weight: 600;
            font-size: 14px;
          }
          
          .custom-amount-input {
            position: relative;
          }
          
          .current-balance {
            font-size: 20px;
            font-weight: 500;
            color: #3D5170;
          }
          
          .discount-badge {
            display: inline-block;
            padding: 2px 6px;
            font-size: 11px;
            font-weight: 600;
            color: #21AA73;
            background-color: rgba(33, 170, 115, 0.1);
            border-radius: 10px;
            margin-left: 5px;
          }
          
          .horizontal-group {
            display: flex;
            align-items: center;
          }
          
          .horizontal-group .current-balance-container {
            flex: 1;
          }
          
          .note-tabs {
            display: flex;
            border-bottom: 1px solid #eaedf3;
            margin-bottom: 10px;
          }
          
          .note-tab {
            padding: 6px 12px;
            font-size: 13px;
            color: #495057;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            transition: all 0.2s ease;
            white-space: nowrap;
          }
          
          .note-tab.active {
            color: #21AA73;
            border-bottom: 2px solid #21AA73;
            font-weight: 500;
          }
          
          .note-tab:hover:not(.active) {
            color: #3D5170;
            background-color: rgba(33, 170, 115, 0.03);
          }
          
          .note-option {
            padding: 8px 12px;
            margin-bottom: 5px;
            border: 1px solid #eaedf3;
            border-radius: 4px;
            font-size: 13px;
            color: #495057;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .note-option.selected {
            background-color: rgba(33, 170, 115, 0.1);
            border-color: rgba(33, 170, 115, 0.3);
          }
          
          .note-option:hover:not(.selected) {
            background-color: #f8f9fa;
          }
        `}
      </style>
      <ModalHeader toggle={toggle}>Adjust Balance</ModalHeader>
      <ModalBody className="p-3">
        <div className="horizontal-group mb-3">
          <div className="current-balance-container">
            <div className="field-label">Current Balance</div>
            <div className="current-balance">${getCurrentTotal()}</div>
          </div>
          <div className="amount-summary-box" style={{width: '170px', marginTop: 0}}>
            <div className="amount-row">
              <div>Discount:</div>
              <div>-${calculateDiscount()}</div>
            </div>
            <div className="amount-row total">
              <div>New:</div>
              <div style={{color: '#21AA73'}}>${parseFloat(newAmount).toFixed(2)}</div>
            </div>
          </div>
        </div>
        
        {/* Percentage-based adjustment section */}
        <div 
          className={`adjustment-section ${adjustmentType === 'percentage' ? 'active' : ''}`}
          onClick={() => setAdjustmentType('percentage')}
        >
          <div className="adjustment-section-title">
            <div className={`radio ${adjustmentType === 'percentage' ? 'selected' : ''}`}></div>
            Quick Discount Options
          </div>
          
          <div className="percentage-buttons">
            <div 
              className={`percentage-button ${selectedPercentage === 10 ? 'active' : ''}`}
              onClick={() => {
                setSelectedPercentage(10);
                setAdjustmentType('percentage');
              }}
            >
              10%
            </div>
            <div 
              className={`percentage-button ${selectedPercentage === 25 ? 'active' : ''}`}
              onClick={() => {
                setSelectedPercentage(25);
                setAdjustmentType('percentage');
              }}
            >
              25%
            </div>
            <div 
              className={`percentage-button ${selectedPercentage === 50 ? 'active' : ''}`}
              onClick={() => {
                setSelectedPercentage(50);
                setAdjustmentType('percentage');
              }}
            >
              50%
            </div>
            <div 
              className={`percentage-button ${selectedPercentage === 75 ? 'active' : ''}`}
              onClick={() => {
                setSelectedPercentage(75);
                setAdjustmentType('percentage');
              }}
            >
              75%
            </div>
          </div>
        </div>
        
        {/* Custom amount section */}
        <div 
          className={`adjustment-section ${adjustmentType === 'custom' ? 'active' : ''}`}
          onClick={handleSelectCustom}
        >
          <div className="adjustment-section-title">
            <div className={`radio ${adjustmentType === 'custom' ? 'selected' : ''}`}></div>
            Custom Amount
          </div>
          
          <div className="custom-amount-input">
            <InputGroup>
              <InputGroupAddon type="prepend">
                <InputGroupText>$</InputGroupText>
              </InputGroupAddon>
              <FormInput
                id={'new-balance'}
                value={displayAmount}
                onChange={(event) => {
                  handleCustomAmountChange(event.target.value);
                  setAdjustmentType('custom');
                }}
                style={{height: '38px'}}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectCustom();
                }}
                placeholder={adjustmentType === 'custom' ? "Enter amount" : ""}
              />
            </InputGroup>
            {displayPercentage > 0 && (
              <div className="text-right mt-1">
                <span className="discount-badge">{displayPercentage}% off</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Adjustment note section */}
        <div>
          <div className="field-label mb-2">Adjustment Note (seen by customer)</div>
          
          <div className="note-tabs">
            <div 
              className={`note-tab ${selectedNoteIndex === -1 ? 'active' : ''}`}
              onClick={() => handleSelectNote(-1)}
            >
              Custom Note
            </div>
            <div 
              className={`note-tab ${selectedNoteIndex !== -1 ? 'active' : ''}`}
              onClick={() => selectedNoteIndex === -1 ? handleSelectNote(0) : null}
            >
              Pre-defined Notes
            </div>
          </div>
          
          {selectedNoteIndex === -1 ? (
            <FormTextarea
              id={'adjust-note'}
              value={adjustmentNote}
              onChange={(event) => setAdjustmentNote(event.target.value)}
              style={{height: 80}}
              placeholder="Enter custom reason for adjustment..."
            />
          ) : (
            <div>
              {PREDEFINED_NOTES.map((note, index) => (
                <div 
                  key={index}
                  className={`note-option ${selectedNoteIndex === index ? 'selected' : ''}`}
                  onClick={() => handleSelectNote(index)}
                >
                  {note}
                </div>
              ))}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter className="py-2">
        <Button
          theme="light"
          onClick={toggle}
          className={'mr-2'}
          size="sm"
        >
          Cancel
        </Button>
        <Button
          theme="accent"
          onClick={handleAdjust}
          size="sm"
        >
          Save Changes
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AdjustBalanceModal; 